import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle, ChevronDown, Download } from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";
import { LeadMagnetModal } from "@/components/LeadMagnetModal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (d = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.85, delay: d, ease: "easeOut" } }),
};

const TRUST_ITEMS = [
  "University of Toronto — High Distinction",
  "Dean's List Honouree",
  "High-Stakes Professional Communication Experience",
  "1:1 Interview & Communication Coaching",
  "100+ Professionals Advised",
];

const WHO_FOR = [
  { title: "Finding Your Direction", body: "Unsure what your next step should be, or whether your current path still fits." },
  { title: "Navigating a Layoff or Career Reset", body: "Unexpected disruption can leave you questioning where you fit. We help you clarify your value and reposition with strategy and confidence." },
  { title: "Pursuing a Promotion", body: "Ready to move up and need a clear strategy to position yourself for advancement." },
  { title: "Preparing for a Key Interview", body: "A major opportunity is approaching and you want to walk in fully prepared." },
  { title: "Changing Industries", body: "Repositioning your experience and skills for a meaningful new direction." },
  { title: "Struggling to Communicate Your Value", body: "You are capable — but not sure how to convey it on paper or in conversation." },
];

const PILLARS = [
  { title: "Career Growth", body: "For young professionals seeking direction, recovering from disruption, pursuing advancement, or building a clearer plan for what comes next.", link: "/booking?service=repositioning-intensive" },
  { title: "Professional Positioning", body: "Strengthen your resume, LinkedIn, and professional narrative so your experience speaks for itself.", link: "/booking?service=resume-repositioning" },
  { title: "Interview Excellence", body: "Prepare for high-stakes conversations. Build confidence, refine your answers, and communicate your value clearly.", link: "/booking?service=interview-strategy" },
];

const DEFAULT_TESTIMONIALS = [
  { quote: "I came in not knowing what step to take next. Within a short time, I had a clear direction, a repositioned resume, and a much better understanding of how to move forward. It made a tangible difference in how I approached my job search.", attribution: "— Daniel, Recent Graduate (Accounting)", service: "Career Direction & Repositioning" },
  { quote: "Julia helped me refine my interviewing skills, offering both insightful feedback and a deep understanding of my areas for improvement. Without hesitation, I would recommend her to anyone looking to strengthen their professional skills and build confidence.", attribution: "— Adriana, Internal Corporate Recruitment", service: "Interview Strategy Intensive" },
  { quote: "After being laid off from working in the same job for five years, I felt completely uncertain about what direction to take. Julia helped me step back and assess my situation properly, without rushing into decisions. That clarity changed how I approached everything that followed.", attribution: "— Sarah, Career Transition", service: "Career Direction & Repositioning" },
  { quote: "I knew I had solid experience, but I wasn't presenting it well. Julia helped me rework my resume and reframe how I talked about my background — it changed how I came across in interviews and on paper.", attribution: "— Marcus, Legal Assistant", service: "Professional Narrative & Resume" },
];

const FAQ_ITEMS = [
  { q: "What is the difference between the Complimentary Call and the Repositioning Intensive?", a: "The Complimentary Advisory Call is a focused 30-minute diagnostic — designed to assess your situation, identify gaps, and clarify what would be most useful. The Professional Repositioning Intensive is a comprehensive engagement that includes a full career assessment, strategy session, resume and LinkedIn review, professional narrative audit, and a customized action plan. Most clients begin with the Complimentary Call to determine which service fits their situation best." },
  { q: "I was recently laid off. Can this advisory support help me?", a: "Yes — and many clients reach out in exactly this situation. A layoff is disorienting, but it is also an opportunity to reassess, reposition, and move forward more intentionally. Julia works with professionals navigating job loss, unexpected transitions, and career resets — helping them clarify their value, rebuild their direction, and approach the next opportunity with confidence and strategy rather than urgency." },
  { q: "Is this only for young professionals?", a: "The firm's focus is young professionals in the first 3 to 8 years of their careers, but Julia works with people at various career stages. If you are navigating a transition, repositioning after a setback, or need clarity and strategy at any stage — the advisory services are available to you." },
  { q: "How many sessions will I need?", a: "Most clients see meaningful progress within a single focused engagement. The Professional Repositioning Intensive is structured to be comprehensive and includes a follow-up advisory session. Some clients return for targeted support around specific challenges — a key interview, a promotion conversation, or a career pivot." },
  { q: "Do you work with clients outside of Toronto or Canada?", a: "Yes. All sessions are conducted virtually via Zoom, and clients are welcome from anywhere. The approach is tailored to your specific situation regardless of location or industry." },
  { q: "How do I know if I am ready to book?", a: "If you are feeling stuck, if career conversations are not going the way you expected, or if you simply want to approach your career more intentionally — you are ready. Start with the Complimentary Advisory Call if you are unsure. There is no obligation and no pressure." },
];

export default function HomePage() {
  const [testimonials, setTestimonials] = useState(DEFAULT_TESTIMONIALS);
  const [flagship, setFlagship] = useState(null);
  const [supportServices, setSupportServices] = useState([]);
  const [showLeadMagnet, setShowLeadMagnet] = useState(false);
  const [hero, setHero] = useState({
    headline: "Strategic Career Advisory for Young Professionals Ready for What's Next",
    subheadline: "Strategic Career Advisory for Young Professionals",
    body: "De Nicola Consulting helps young professionals gain clarity, navigate career disruption and uncertainty, strengthen their professional positioning, and move forward with confidence through practical, thoughtful career strategy.",
  });

  // Intentional: empty dep array runs once on mount to set page title and fetch services; re-running on every render would cause unnecessary API calls.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    document.title = "De Nicola Consulting Co. | Strategic Career Advisory for Young Professionals";
    axios.get(`${API}/services`).then(res => {
      setFlagship(res.data.find(s => s.id === "repositioning-intensive") || null);
      setSupportServices(res.data.filter(s => s.id !== "repositioning-intensive"));
    }).catch(() => {});
    axios.get(`${API}/content`).then(res => {
      if (res.data.testimonials?.length) setTestimonials(res.data.testimonials);
      if (res.data.hero?.headline) setHero(prev => ({ ...prev, ...res.data.hero }));
    }).catch(() => {});
  }, []);

  return (
    <div className="bg-ivory min-h-screen">

      {/* ── 1. Hero ────────────────────────────────────────────────────────── */}
      <section className="min-h-[92vh] flex flex-col justify-center relative overflow-hidden bg-ivory" data-testid="hero-section">
        <div className="absolute inset-0 opacity-[0.035]" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1681488262364-bd33ac2d2de4?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600')", backgroundSize: "cover", backgroundPosition: "center 30%" }} />
        <div className="relative z-10 max-w-5xl mx-auto px-6 lg:px-12 py-28 text-center">
          <motion.p variants={fadeUp} initial="hidden" animate="visible" custom={0}
            className="font-lora text-[10px] tracking-[0.32em] uppercase text-[#C2B8A3] mb-8">
            {hero.subheadline}
          </motion.p>
          <motion.h1 variants={fadeUp} initial="hidden" animate="visible" custom={0.18}
            className="font-lora text-4xl sm:text-5xl lg:text-[3.4rem] text-[#001F3F] leading-tight font-normal mb-8"
            data-testid="hero-headline">
            {hero.headline}
          </motion.h1>
          <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={0.35}>
            <div className="w-12 h-px bg-[#C2B8A3] mx-auto mb-8" />
          </motion.div>
          <motion.p variants={fadeUp} initial="hidden" animate="visible" custom={0.45}
            className="text-[#001F3F]/65 text-base leading-relaxed max-w-2xl mx-auto mb-12 font-serif">
            {hero.body}
          </motion.p>
          <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={0.62}
            className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
            <Link to="/booking?service=intro-call"
              className="inline-flex items-center justify-center gap-2 bg-[#001F3F] text-white px-10 py-4 text-xs tracking-[0.18em] uppercase hover:bg-[#001F3F]/90 transition-all duration-300 group"
              data-testid="hero-cta-button">
              Book a Complimentary Advisory Call <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <button onClick={() => setShowLeadMagnet(true)}
              className="inline-flex items-center justify-center gap-2 border border-[#001F3F] text-[#001F3F] px-10 py-4 text-xs tracking-[0.18em] uppercase hover:bg-[#001F3F] hover:text-white transition-all duration-300"
              data-testid="hero-lead-magnet-btn">
              <Download size={14} /> Free Career Roadmap
            </button>
          </motion.div>
          <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={0.78}
            className="flex flex-wrap justify-center gap-x-6 gap-y-2">
            {["University of Toronto Alumna", "7+ Years 1:1 Advisory", "100+ Clients Supported"].map((t, i) => (
              <span key={t} className="text-[#001F3F]/30 font-serif text-xs tracking-wide">{t}</span>
            ))}
          </motion.div>
        </div>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[#001F3F]/25 animate-bounce">
          <ChevronDown size={20} />
        </div>
      </section>

      {/* ── 2. Trust Bar ───────────────────────────────────────────────────── */}
      <section className="bg-white border-y border-[#E0DDD8] py-5 px-6 overflow-x-hidden" data-testid="trust-bar">
        <div className="max-w-5xl mx-auto flex flex-wrap justify-center items-center gap-x-7 gap-y-3">
          {TRUST_ITEMS.map((item, i) => (
            <React.Fragment key={item}>
              <span className="font-lora text-[#001F3F]/45 text-[10px] tracking-[0.18em] uppercase sm:whitespace-nowrap text-center">{item}</span>
              {i < TRUST_ITEMS.length - 1 && <span className="hidden sm:block text-[#C2B8A3]/50 text-xs select-none">·</span>}
            </React.Fragment>
          ))}
        </div>
      </section>

      {/* ── 3. Who This Is For ─────────────────────────────────────────────── */}
      <section className="py-24 px-6 lg:px-12 bg-ivory" data-testid="who-for-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.8 }}
              className="font-lora text-[10px] tracking-[0.3em] uppercase text-[#C2B8A3] mb-4">Who This Is For</motion.p>
            <motion.h2 initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.15 }}
              className="font-lora text-3xl lg:text-4xl text-[#001F3F] font-normal">
              You Have What It Takes.<br className="hidden sm:block" /> You Just Need Clarity.
            </motion.h2>
            <div className="w-10 h-px bg-[#C2B8A3] mx-auto mt-5 mb-5" />
            <p className="text-[#001F3F]/55 font-serif text-sm max-w-lg mx-auto leading-relaxed">
              If any of the following resonates, you are in the right place.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {WHO_FOR.map((item, i) => (
              <motion.div key={item.title}
                initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.07 }}
                className="bg-white border border-[#E0DDD8] p-6 hover:border-[#C2B8A3]/70 transition-all duration-300">
                <div className="w-5 h-px bg-[#C2B8A3] mb-4" />
                <h3 className="font-lora text-[#001F3F] text-base font-normal mb-2">{item.title}</h3>
                <p className="text-[#001F3F]/55 font-serif text-sm leading-relaxed">{item.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 4. How We Help — 3 Pillars ─────────────────────────────────────── */}
      <section className="bg-[#001F3F] py-24 px-6 lg:px-12" data-testid="pillars-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.8 }}
              className="font-lora text-[10px] tracking-[0.3em] uppercase text-[#C2B8A3] mb-4">How We Help</motion.p>
            <motion.h2 initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.15 }}
              className="font-lora text-3xl lg:text-4xl text-white font-normal">
              Three Areas of Strategic Advisory
            </motion.h2>
            <div className="w-10 h-px bg-[#C2B8A3] mx-auto mt-5" />
          </div>
          <div className="grid sm:grid-cols-3 gap-7">
            {PILLARS.map((pillar, i) => (
              <motion.div key={pillar.title}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: i * 0.14 }}
                className="border border-white/10 p-8 hover:border-[#C2B8A3]/40 transition-all duration-300">
                <div className="w-7 h-px bg-[#C2B8A3] mb-5" />
                <h3 className="font-lora text-white text-xl font-normal mb-3">{pillar.title}</h3>
                <p className="text-white/55 font-serif text-sm leading-relaxed mb-6">{pillar.body}</p>
                <Link to={pillar.link} className="text-[#C2B8A3] text-[10px] tracking-widest uppercase hover:opacity-70 transition-opacity">
                  Explore →
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 5. Services — Flagship + Support ──────────────────────────────── */}
      <section className="py-24 px-6 lg:px-12 bg-ivory" data-testid="services-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.8 }}
              className="font-lora text-[10px] tracking-[0.3em] uppercase text-[#C2B8A3] mb-4">Advisory Services</motion.p>
            <motion.h2 initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.15 }}
              className="font-lora text-3xl lg:text-4xl text-[#001F3F] font-normal">
              Where Would You Like to Begin?
            </motion.h2>
            <div className="w-10 h-px bg-[#C2B8A3] mx-auto mt-5" />
          </div>

          {/* Flagship */}
          {flagship && (
            <motion.div initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}
              className="bg-white border-t-2 border-[#C2B8A3] shadow-lg p-10 lg:p-14 mb-8 relative" data-testid="flagship-service-card">
              <div className="absolute top-5 right-6 flex flex-col items-end gap-1.5">
                <span className="bg-[#C2B8A3] text-[#001F3F] text-[10px] tracking-widest uppercase px-3 py-1 font-medium">Most Popular</span>
                {flagship.sale_label && (
                  <span className="border border-[#C2B8A3] text-[#C2B8A3] font-serif text-[10px] tracking-[0.18em] uppercase px-2.5 py-0.5">{flagship.sale_label}</span>
                )}
              </div>
              <div className="grid lg:grid-cols-2 gap-12">
                <div>
                  <p className="font-lora text-[10px] tracking-[0.24em] uppercase text-[#C2B8A3] mb-3">Signature Offer</p>
                  <h3 className="font-lora text-3xl text-[#001F3F] font-normal mb-2">{flagship.name}</h3>
                  <p className="font-lora text-2xl text-[#001F3F] mb-6">
                    ${flagship.price}{" "}
                    <span className="text-base font-normal text-[#001F3F]/50">{flagship.currency}</span>
                  </p>
                  <p className="text-[#001F3F]/65 leading-relaxed font-serif mb-8">{flagship.description}</p>
                  <Link to={`/booking?service=${flagship.id}`}
                    className="inline-flex items-center gap-2 bg-[#001F3F] text-white px-8 py-3.5 text-xs tracking-[0.18em] uppercase hover:bg-[#001F3F]/90 transition-all group"
                    data-testid="flagship-book-btn">
                    Book This Service <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
                <div>
                  {flagship.includes?.length > 0 && (
                    <>
                      <p className="font-lora text-[10px] tracking-[0.22em] uppercase text-[#001F3F]/45 mb-4">This Service Includes</p>
                      <ul className="space-y-3">
                        {flagship.includes.map((item) => (
                          <li key={item} className="flex items-start gap-3 text-[#001F3F]/65 text-sm font-serif">
                            <CheckCircle size={15} className="text-[#C2B8A3] mt-0.5 shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                  {flagship.outcome && (
                    <p className="mt-6 text-[#001F3F]/45 text-sm font-serif italic border-l-2 border-[#C2B8A3] pl-4">{flagship.outcome}</p>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          <p className="text-center text-[#001F3F]/40 text-sm font-serif italic mb-5">Looking for something more focused?</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {supportServices.map((svc, i) => (
              <motion.div key={svc.id}
                initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.1 }}
                className={`border p-6 transition-all duration-300 ${svc.price === 0 ? "bg-[#F4F3EF] border-[#C2B8A3]/60" : "bg-white border-[#E0DDD8] hover:border-[#C2B8A3]"}`}
                data-testid={`service-card-${svc.id}`}>
                {svc.price === 0 && <p className="font-lora text-[10px] tracking-[0.22em] uppercase text-[#C2B8A3] mb-1">Begin Here</p>}
                <p className="font-lora text-[#001F3F] text-base font-normal mb-1">{svc.name}</p>
                <p className="font-lora text-lg text-[#C2B8A3] mb-3">{svc.price === 0 ? "Complimentary" : `$${svc.price} ${svc.currency}`}</p>
                <p className="text-[#001F3F]/55 text-sm font-serif leading-relaxed mb-4">{svc.description}</p>
                <Link to={`/booking?service=${svc.id}`} className="text-[#001F3F] text-[10px] tracking-widest uppercase hover:text-[#C2B8A3] transition-colors">
                  Book →
                </Link>
              </motion.div>
            ))}
          </div>

          {/* Tailored inquiry pathway */}
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.2 }}
            className="mt-14 pt-10 border-t border-[#E0DDD8] text-center">
            <p className="font-serif text-[#001F3F]/45 text-sm mb-3">
              Looking for ongoing support or a more customized engagement?
            </p>
            <Link to="/contact"
              className="inline-flex items-center gap-2 text-[#001F3F]/60 text-[10px] tracking-[0.22em] uppercase hover:text-[#001F3F] transition-colors duration-300 group"
              data-testid="tailored-inquiry-link">
              Inquire About Tailored Advisory <ArrowRight size={11} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>

        </div>
      </section>

      {/* ── 6. Lead Magnet ────────────────────────────────────────────────── */}
      <section className="py-20 px-6 lg:px-12 bg-[#001F3F]" data-testid="lead-magnet-section">
        <div className="max-w-4xl mx-auto text-center">
          <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.8 }}
            className="font-lora text-[10px] tracking-[0.3em] uppercase text-[#C2B8A3] mb-4">Free Resource</motion.p>
          <motion.h2 initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.15 }}
            className="font-lora text-3xl lg:text-4xl text-white font-normal mb-5">
            Free Career Roadmap for Young Professionals
          </motion.h2>
          <div className="w-10 h-px bg-[#C2B8A3] mx-auto mb-7" />
          <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.25 }}
            className="text-white/55 font-serif text-base leading-relaxed mb-8 max-w-2xl mx-auto">
            A clear five-step framework to assess where you stand, define your direction, strengthen your positioning, and begin moving forward. You do not need to figure this out alone. Start here.
          </motion.p>
          <motion.button initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.35 }}
            onClick={() => setShowLeadMagnet(true)}
            className="inline-flex items-center gap-2 bg-[#C2B8A3] text-[#001F3F] px-10 py-4 text-xs tracking-[0.18em] uppercase hover:bg-[#C2B8A3]/85 transition-all"
            data-testid="lead-magnet-cta-btn">
            <Download size={14} /> Download Free Roadmap
          </motion.button>
          <p className="text-white/25 font-serif text-xs mt-4">Free. No commitment required.</p>
        </div>
      </section>

      {/* ── 7. Social Proof ───────────────────────────────────────────────── */}
      <section className="py-24 px-6 lg:px-12 bg-white" data-testid="testimonials-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.8 }}
              className="font-lora text-[10px] tracking-[0.3em] uppercase text-[#C2B8A3] mb-4">Client Experiences</motion.p>
            <motion.h2 initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.15 }}
              className="font-lora text-3xl lg:text-4xl text-[#001F3F] font-normal">
              What Clarity and Strategy Changed
            </motion.h2>
            <div className="w-10 h-px bg-[#C2B8A3] mx-auto mt-5" />
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            {testimonials.slice(0, 4).map((t, i) => (
              <motion.div key={t.attribution || i}
                initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: i * 0.1 }}
                className="border border-[#E0DDD8] p-8 bg-[#FAFAF8]">
                <p className="text-[#C2B8A3] text-2xl font-lora leading-none mb-4 select-none">"</p>
                <p className="text-[#001F3F]/70 font-serif text-sm leading-relaxed mb-6">{t.quote}</p>
                <div className="border-t border-[#E0DDD8] pt-4">
                  <p className="text-[#001F3F]/50 font-serif text-xs">{t.attribution}</p>
                  {t.service && <p className="text-[#C2B8A3] font-lora text-[10px] tracking-widest uppercase mt-1">{t.service}</p>}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 8. Founder Credibility ────────────────────────────────────────── */}
      <section className="py-24 px-6 lg:px-12 bg-[#F4F3EF]" data-testid="founder-section">
        <div className="max-w-5xl mx-auto grid lg:grid-cols-2 gap-16 items-start">
          <div>
            <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.8 }}
              className="font-lora text-[10px] tracking-[0.3em] uppercase text-[#C2B8A3] mb-4">About the Founder</motion.p>
            <motion.h2 initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.15 }}
              className="font-lora text-3xl text-[#001F3F] font-normal mb-5">Julia De Nicola</motion.h2>
            <div className="w-10 h-px bg-[#C2B8A3] mb-7" />
            <p className="text-[#001F3F]/65 font-serif text-base leading-relaxed mb-5">
              Julia De Nicola is a career advisor, strategist, and mentor who works with young professionals navigating the most formative — and sometimes most difficult — years of their careers.
            </p>
            <p className="text-[#001F3F]/65 font-serif text-base leading-relaxed mb-5">
              Her approach is grounded in academic rigour, practical strategy, and a genuine belief that clarity — not luck — is what separates professionals who move forward from those who stay stuck.
            </p>
            <p className="text-[#001F3F]/65 font-serif text-base leading-relaxed mb-8">
              Julia brings years of direct one-on-one mentorship and coaching experience — including structured interview preparation and communication development work — helping individuals navigate high-stakes professional moments with confidence. That depth of advisory experience shapes every client engagement.
            </p>
            <Link to="/about"
              className="inline-flex items-center gap-2 border border-[#001F3F] text-[#001F3F] px-7 py-3 text-xs tracking-[0.18em] uppercase hover:bg-[#001F3F] hover:text-white transition-all duration-300 group"
              data-testid="founder-about-link">
              Read the Full Story <ArrowRight size={13} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
          <div className="space-y-3">
            <p className="font-lora text-[10px] tracking-[0.22em] uppercase text-[#001F3F]/40 mb-5">Credentials & Background</p>
            {[
              "University of Toronto — Graduate with High Distinction",
              "Dean's List Honouree",
              "High-Stakes Professional Communication Experience",
              "Litigation Legal Assistant",
              "1:1 Interview Coaching & Communication Development",
              "Mentorship & Career Advising — Students and Young Professionals",
            ].map((cred, i) => (
              <motion.div key={cred}
                initial={{ opacity: 0, x: 12 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.07 }}
                className="flex items-start gap-3 py-3 border-b border-[#E0DDD8]">
                <div className="w-1 h-1 rounded-full bg-[#C2B8A3] mt-2 shrink-0" />
                <p className="text-[#001F3F]/70 font-serif text-sm">{cred}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 9. FAQ ────────────────────────────────────────────────────────── */}
      <section className="py-24 px-6 lg:px-12 bg-ivory" data-testid="faq-section">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-14">
            <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.8 }}
              className="font-lora text-[10px] tracking-[0.3em] uppercase text-[#C2B8A3] mb-4">Common Questions</motion.p>
            <motion.h2 initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.15 }}
              className="font-lora text-3xl lg:text-4xl text-[#001F3F] font-normal">
              Frequently Asked Questions
            </motion.h2>
            <div className="w-10 h-px bg-[#C2B8A3] mx-auto mt-5" />
          </div>
          <Accordion type="single" collapsible className="space-y-2">
            {FAQ_ITEMS.map((item, i) => (
              <AccordionItem key={item.q} value={`faq-${i}`} className="border border-[#E0DDD8] bg-white px-6">
                <AccordionTrigger className="font-lora text-[#001F3F] text-sm font-normal text-left py-5 hover:no-underline">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-[#001F3F]/60 font-serif text-sm leading-relaxed pb-5">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ── 10. Final CTA ─────────────────────────────────────────────────── */}
      <section className="py-28 px-6 bg-[#001F3F] text-center" data-testid="final-cta-section">
        <div className="max-w-3xl mx-auto">
          <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.8 }}
            className="font-lora text-[10px] tracking-[0.3em] uppercase text-[#C2B8A3] mb-5">Take the First Step</motion.p>
          <motion.h2 initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.15 }}
            className="font-lora text-3xl lg:text-4xl text-white font-normal mb-6">
            Your Situation Can Improve.<br className="hidden sm:block" /> There Is a Path Forward.
          </motion.h2>
          <div className="w-10 h-px bg-[#C2B8A3] mx-auto mb-7" />
          <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.25 }}
            className="text-white/55 font-serif text-base leading-relaxed mb-10 max-w-xl mx-auto">
            Begin with a no-obligation Complimentary Advisory Call — a focused 30-minute conversation to assess where you stand and clarify what would be most useful to address first.
          </motion.p>
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.35 }}
            className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/booking?service=intro-call"
              className="inline-flex items-center justify-center gap-2 bg-white text-[#001F3F] px-10 py-4 text-xs tracking-[0.18em] uppercase hover:bg-white/90 transition-all group"
              data-testid="final-cta-button">
              Book a Complimentary Advisory Call <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/services"
              className="inline-flex items-center justify-center gap-2 border border-white/30 text-white/70 px-10 py-4 text-xs tracking-[0.18em] uppercase hover:border-white/60 hover:text-white transition-all">
              Explore Services →
            </Link>
          </motion.div>
        </div>
      </section>

      <LeadMagnetModal open={showLeadMagnet} onClose={() => setShowLeadMagnet(false)} />
    </div>
  );
}
